import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconCalendar, IconFileText, IconTrash, IconUpload, IconX } from "@tabler/icons-react"
import { format, parse } from "date-fns"
import { useEffect, useState } from "react"
import type { DateRange } from "react-day-picker"

interface Attachment {
  id: number
  filename: string
  byte_size: number
  created_at: string
  view_url: string
  download_url: string
}

interface Balance {
  id: number
  balance_type: string
  year: number
  days_available: number
  days_used: number
  remaining_days: number
}

interface TimeOffRequest {
  id: number
  request_type: string
  request_type_unit: string
  start_date: string
  end_date: string
  hours_requested: number | null
  notes: string | null
  status: string
  attachments: Attachment[]
}

interface Props {
  [key: string]: unknown
  time_off_request: TimeOffRequest
  balances: Balance[]
  contract_hours_per_day: number
}

interface CalcResult {
  business_days: number
  total_hours: number
  holidays: string[]
}

function formatDays(value: number | string) {
  const n = Number(value)
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)
}

const REQUEST_TYPES = [
  { value: "holiday", label: "Holiday" },
  { value: "parental", label: "Parental" },
  { value: "study", label: "Study" },
  { value: "unpaid", label: "Unpaid" },
]

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

export default function TimeOffRequestEdit() {
  const { time_off_request: request, balances, contract_hours_per_day } = usePage<Props>().props
  const isApproved = request.status === "approved"

  const [data, setFormData] = useState({
    request_type: request.request_type,
    request_type_unit: request.request_type_unit || "days",
    start_date: request.start_date,
    end_date: request.end_date,
    hours_requested: request.hours_requested || ("" as string | number),
    notes: request.notes || "",
  })
  const [processing, setProcessing] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  function setData<K extends keyof typeof data>(key: K, value: (typeof data)[K]) {
    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  const [calculation, setCalculation] = useState<CalcResult | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(request.attachments)

  useEffect(() => {
    if (data.start_date && data.end_date) {
      fetchCalculation()
    }
  }, [data.start_date, data.end_date])

  async function fetchCalculation() {
    try {
      const res = await fetch(`/calculate_business_days?start_date=${data.start_date}&end_date=${data.end_date}`)
      if (res.ok) {
        const result = await res.json()
        setCalculation(result)
      }
    } catch {
      // silently fail
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    setErrors({})

    const formData = new FormData()
    formData.append("_method", "put")
    formData.append("time_off_request[request_type]", data.request_type)
    formData.append("time_off_request[request_type_unit]", data.request_type_unit)
    formData.append("time_off_request[start_date]", data.start_date)
    formData.append("time_off_request[end_date]", data.end_date)
    formData.append("time_off_request[hours_requested]", String(data.hours_requested))
    formData.append("time_off_request[notes]", data.notes)
    selectedFiles.forEach((file) => {
      formData.append("time_off_request[files][]", file)
    })

    router.post(`/time_off_requests/${request.id}`, formData as any, {
      forceFormData: true,
      onError: (errs) => {
        setErrors(errs as Record<string, string>)
      },
      onFinish: () => {
        setProcessing(false)
      },
    })
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || [])
    setSelectedFiles((prev) => [...prev, ...files])
  }

  function removeFile(index: number) {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    setSelectedFiles((prev) => [...prev, ...files])
  }

  async function removeExistingAttachment(attachmentId: number) {
    if (!confirm("Remove this attachment?")) return
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
      const res = await fetch(`/purge_attachment/${attachmentId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken },
      })
      if (res.ok) {
        setExistingAttachments((prev) => prev.filter((a) => a.id !== attachmentId))
      }
    } catch {
      // silently fail
    }
  }

  const groupedBalances = balances.reduce<Record<string, Balance[]>>((acc, b) => {
    if (!acc[b.balance_type]) acc[b.balance_type] = []
    acc[b.balance_type].push(b)
    return acc
  }, {})

  const allErrors = Object.values(errors)

  return (
    <>
      <Head title={`Edit Request #${request.id} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* Header */}
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/time_off_requests">My Requests</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={`/time_off_requests/${request.id}`}>#{request.id}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={`/time_off_requests/${request.id}`}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Edit Request #{request.id}</h1>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          {/* Form */}
          <div className="xl:col-span-7">
            <form onSubmit={handleSubmit}>
              <div className="rounded-xl border bg-card">
                <div className="p-6">
                  {allErrors.length > 0 && (
                    <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                      <ul className="space-y-1 text-sm text-destructive">
                        {allErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {isApproved && (
                    <div className="bg-muted mb-6 rounded-lg px-4 py-3">
                      <p className="text-muted-foreground text-sm">
                        Approved requests allow editing notes and attachments only.
                      </p>
                    </div>
                  )}

                  <div className="space-y-5">
                    {/* Type + Unit */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="request_type">Type</Label>
                        <Select
                          value={data.request_type}
                          onValueChange={(v) => setData("request_type", v)}
                          disabled={isApproved}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type..." />
                          </SelectTrigger>
                          <SelectContent>
                            {REQUEST_TYPES.map((t) => (
                              <SelectItem key={t.value} value={t.value}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="request_type_unit">Unit</Label>
                        <Select
                          value={data.request_type_unit}
                          onValueChange={(v) => setData("request_type_unit", v)}
                          disabled={isApproved}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="days">Days</SelectItem>
                            <SelectItem value="hours">Hours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {data.request_type_unit === "hours" && (
                      <div className="space-y-2">
                        <Label htmlFor="hours_requested">Hours</Label>
                        <Input
                          id="hours_requested"
                          type="number"
                          step="0.5"
                          min="0.5"
                          placeholder="0"
                          value={data.hours_requested}
                          onChange={(e) => setData("hours_requested", e.target.value as any)}
                          disabled={isApproved}
                        />
                        <p className="text-muted-foreground text-xs">Contract: {contract_hours_per_day}h/day</p>
                      </div>
                    )}

                    <Separator />

                    {/* Date Range */}
                    <div className="space-y-2">
                      <Label>Dates</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                            disabled={isApproved}
                          >
                            <IconCalendar className="mr-2 h-4 w-4" />
                            {data.start_date && data.end_date ? (
                              <>
                                {format(parse(data.start_date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")}
                                {" – "}
                                {format(parse(data.end_date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")}
                              </>
                            ) : data.start_date ? (
                              <>
                                {format(parse(data.start_date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")}
                                {" – Select end date"}
                              </>
                            ) : (
                              <span className="text-muted-foreground">Pick a date range</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="range"
                            numberOfMonths={2}
                            selected={
                              data.start_date
                                ? {
                                    from: parse(data.start_date, "yyyy-MM-dd", new Date()),
                                    to: data.end_date ? parse(data.end_date, "yyyy-MM-dd", new Date()) : undefined,
                                  }
                                : undefined
                            }
                            onSelect={(range: DateRange | undefined) => {
                              setFormData((prev) => ({
                                ...prev,
                                start_date: range?.from ? format(range.from, "yyyy-MM-dd") : "",
                                end_date: range?.to ? format(range.to, "yyyy-MM-dd") : "",
                              }))
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {calculation && !isApproved && (
                      <div className="bg-muted/50 flex items-center gap-3 rounded-lg px-4 py-3">
                        <IconCalendar className="text-muted-foreground h-4 w-4 shrink-0" />
                        <div className="text-sm">
                          <span className="font-medium">
                            {calculation.business_days} business day{calculation.business_days !== 1 ? "s" : ""}
                          </span>
                          {data.request_type_unit === "hours" && (
                            <span className="text-muted-foreground"> &middot; {calculation.total_hours}h</span>
                          )}
                          {calculation.holidays.length > 0 && (
                            <span className="text-muted-foreground">
                              {" "}
                              &middot; excl. {calculation.holidays.join(", ")}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <Separator />

                    {/* Notes */}
                    <div className="space-y-2">
                      <Label htmlFor="notes">
                        Notes <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <textarea
                        id="notes"
                        rows={3}
                        placeholder="Any additional context..."
                        className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        value={data.notes}
                        onChange={(e) => setData("notes", e.target.value)}
                      />
                    </div>

                    {/* Existing Attachments */}
                    {existingAttachments.length > 0 && (
                      <div className="space-y-2">
                        <Label>Current Attachments</Label>
                        <div className="space-y-1.5">
                          {existingAttachments.map((att) => (
                            <div key={att.id} className="flex items-center justify-between rounded-md border px-3 py-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <IconFileText className="text-muted-foreground h-4 w-4 shrink-0" />
                                <span className="truncate text-sm">{att.filename}</span>
                                <span className="text-muted-foreground shrink-0 text-xs">
                                  {formatBytes(att.byte_size)}
                                </span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0"
                                onClick={() => removeExistingAttachment(att.id)}
                              >
                                <IconTrash className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* New File Upload */}
                    <div className="space-y-2">
                      <Label>Add Attachments</Label>
                      <div
                        className="flex h-24 w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed transition-colors hover:bg-muted/50"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        onClick={() => document.getElementById("file-input")?.click()}
                      >
                        <IconUpload className="text-muted-foreground mb-1 h-5 w-5" />
                        <p className="text-muted-foreground text-xs">
                          Drop files or <span className="text-foreground font-medium">browse</span>
                        </p>
                      </div>
                      <input
                        id="file-input"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                        className="hidden"
                        onChange={handleFileSelect}
                      />

                      {selectedFiles.length > 0 && (
                        <div className="space-y-1.5">
                          {selectedFiles.map((file, i) => (
                            <div key={i} className="flex items-center justify-between rounded-md border px-3 py-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <IconFileText className="text-muted-foreground h-4 w-4 shrink-0" />
                                <span className="truncate text-sm">{file.name}</span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0"
                                onClick={() => removeFile(i)}
                              >
                                <IconX className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <Separator />
                <div className="flex justify-end gap-2 p-4">
                  <Button variant="ghost" asChild>
                    <a href={`/time_off_requests/${request.id}`}>Cancel</a>
                  </Button>
                  <Button type="submit" disabled={processing}>
                    {processing ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            </form>
          </div>

          {/* Balance Sidebar */}
          <div className="xl:col-span-5">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-4 text-sm font-medium">Your Balance</h3>

              {Object.keys(groupedBalances).length > 0 ? (
                <div className="space-y-5">
                  {Object.entries(groupedBalances).map(([type, typeBalances]) => {
                    const sorted = [...typeBalances].sort((a, b) => a.year - b.year)
                    const totalRemaining = sorted.reduce((s, b) => s + Number(b.remaining_days), 0)
                    const totalAvailable = sorted.reduce((s, b) => s + Number(b.days_available), 0)
                    const usedPct =
                      totalAvailable > 0 ? Math.round(((totalAvailable - totalRemaining) / totalAvailable) * 100) : 0

                    return (
                      <div key={type}>
                        <div className="flex items-baseline justify-between">
                          <span className="text-sm font-medium capitalize">{type}</span>
                          <span className="text-muted-foreground text-xs tabular-nums">
                            {formatDays(totalRemaining)} / {formatDays(totalAvailable)} days
                          </span>
                        </div>
                        <div className="bg-muted mt-1.5 h-1.5 w-full overflow-hidden rounded-full">
                          <div
                            className="bg-primary h-full rounded-full transition-all"
                            style={{ width: `${Math.min(100, 100 - usedPct)}%` }}
                          />
                        </div>
                        {sorted.length > 1 && (
                          <div className="mt-2 space-y-0.5">
                            {sorted.map((b) => (
                              <div
                                key={b.id}
                                className="text-muted-foreground flex justify-between text-xs tabular-nums"
                              >
                                <span>{b.year}</span>
                                <span>
                                  {formatDays(b.remaining_days)} / {formatDays(b.days_available)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No balance configured. Contact your admin.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

TimeOffRequestEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
